import { useLocalSearchParams } from 'expo-router';
import { TabList, TabSlot, Tabs, TabTrigger } from 'expo-router/ui';
import { useRef } from 'react';
import { type ScrollView, StyleSheet } from 'react-native';
import { useQuery, useSubscription } from 'urql';
import Nav from '@/components/Custom/Nav';
import * as NavItem from '@/components/Custom/NavItem';
import { graphql, readFragment } from '@/gql/graphql';
import useUserId from '@/hooks/useUserId';
import ScheduleDetailToolbar, {
  ScheduleDetailToolbarFragment,
} from './ScheduleDetailToolbar';

const ScheduleDetailLayoutFragment = graphql(/* GraphQL */ `
  fragment ScheduleDetailLayout on event {
    id
    has_standings
    stat_widget_id
  }
`);

const GetScheduleDetailLayout = graphql(/* GraphQL */ `
  query GetScheduleDetailLayout($eventId: uuid!) {
    event_by_pk(id: $eventId) {
      ...ScheduleDetailToolbar
      ...ScheduleDetailLayout @_optional
    }
  }
`, [ScheduleDetailToolbarFragment, ScheduleDetailLayoutFragment]);

const WatchScheduleDetailLayout = graphql(/* GraphQL */ `
  subscription WatchScheduleDetail($eventId: uuid!) {
    event_by_pk(id: $eventId) {
      ...ScheduleDetailToolbar
      ...ScheduleDetailLayout
    }
  }
`, [ScheduleDetailToolbarFragment, ScheduleDetailLayoutFragment]);

export default function ScheduleDetailLayout({
  basePath = '/organization/schedule/[eventId]',
}: {
  basePath?: string;
}) {
  const navRef = useRef<ScrollView>(null);

  const { eventId } = useLocalSearchParams<{
    eventId: string;
  }>();

  const personId = useUserId();

  const [{ data }] = useQuery({
    query: GetScheduleDetailLayout,
    variables: {
      eventId: eventId || '',
      personId,
    },
    pause: !eventId,
    requestPolicy: 'cache-and-network',
  });

  const layout = readFragment(ScheduleDetailLayoutFragment, data?.event_by_pk);

  useSubscription({
    query: WatchScheduleDetailLayout,
    variables: {
      eventId: eventId || '',
      personId,
    },
    pause: !eventId,
  });

  const tabTriggers = [
    {
      name: 'events',
      shouldRender: true,
      label: 'Events',
    },
    {
      name: 'standings',
      shouldRender: !!layout?.has_standings,
      label: 'Standings',
    },
    {
      name: 'stats',
      shouldRender: !!layout?.stat_widget_id,
      label: 'Stats',
    },
    {
      name: 'schedules',
      shouldRender: true,
      label: 'Schedules',
    },
  ];

  const visibleTabTriggers = tabTriggers.filter(
    (trigger) => trigger.shouldRender,
  );

  return (
    <>
      <ScheduleDetailToolbar fragment={data?.event_by_pk} />
      <Tabs>
        <Nav
          ref={navRef}
          data={visibleTabTriggers}
          keyExtractor={(item) => item.name}
          renderItem={({ item, index }) => (
            <TabTrigger resetOnFocus name={item.name} asChild>
              <NavItem.Container index={index}>
                <NavItem.Label>{item.label}</NavItem.Label>
              </NavItem.Container>
            </TabTrigger>
          )}
        />
        <TabList>
          <TabTrigger
            name="events"
            href={{
              pathname: basePath,
              params: { eventId },
            }}
          />

          <TabTrigger
            name="schedules"
            href={{
              pathname: `${basePath}/schedules`,
              params: { eventId },
            }}
          />

          <TabTrigger
            name="standings"
            href={{
              pathname: `${basePath}/standings`,
              params: { eventId },
            }}
          />

          <TabTrigger
            name="stats"
            href={{
              pathname: `${basePath}/stats`,
              params: { eventId },
            }}
          />
        </TabList>
        <TabSlot style={style.tabSlot} />
      </Tabs>
    </>
  );
}

const style = StyleSheet.create({
  tabSlot: {
    flex: 1,
  },
});
